import React, { useState, useEffect } from "react";
import {
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    Stack,
    Alert,
    CircularProgress,
    LinearProgress
} from "@mui/material";
import {
    Storage as StorageIcon,
    Security as SecurityIcon,
    BugReport as BugReportIcon,
    Speed as SpeedIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Info as InfoIcon
} from "@mui/icons-material";
import { useAuth } from "../../../context/AuthContext";
import { usePermissions } from "../../../utils/permissions";
import { useSerializationAlerts } from "../../../hooks/useSerializationAlerts";

/**
 * Dashboard principal para administradores de sistema
 */
const SystemDashboard = () => {
    const { user } = useAuth();
    const permissions = usePermissions(user);
    const { getAlertsBySeverity } = useSerializationAlerts();
    const [loading, setLoading] = useState(true);
    const [systemStats, setSystemStats] = useState({
        totalEntities: 0,
        serializedEntities: 0,
        pendingMigrations: 0,
        systemErrors: 0,
        activeUsers: 0,
        storageUsage: 0,
        cpuUsage: 0,
        memoryUsage: 0,
        networkLatency: 0
    });

    useEffect(() => {
        loadSystemStats();
    }, []);

    const loadSystemStats = async () => {
        try {
            setLoading(true);
            // TODO: Implementar carga de estadísticas reales del sistema
            // Simular datos para demostración
            setTimeout(() => {
                setSystemStats({
                    totalEntities: 15420,
                    serializedEntities: 15200,
                    pendingMigrations: 3,
                    systemErrors: 2,
                    activeUsers: 89,
                    storageUsage: 67,
                    cpuUsage: 23,
                    memoryUsage: 45,
                    networkLatency: 45
                });
                setLoading(false);
            }, 1000);
        } catch (error) {
            // console.error("Error loading system stats:", error);
            setLoading(false);
        }
    };

    const StatCard = ({ title, value, icon, color, subtitle, progress }) => (
        <Card sx={{ height: "100%" }}>
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                        sx={{
                            backgroundColor: `${color}.light`,
                            borderRadius: "50%",
                            p: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        {icon}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h4" component="div" sx={{ fontWeight: "bold" }}>
                            {value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography variant="caption" color="text.secondary">
                                {subtitle}
                            </Typography>
                        )}
                        {progress !== undefined && (
                            <Box sx={{ mt: 1 }}>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={progress} 
                                    sx={{ height: 6, borderRadius: 3 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {progress}% utilizado
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );

    const QuickActionCard = ({ title, description, icon, action, color = "primary", disabled = false }) => (
        <Card 
            sx={{ 
                height: "100%", 
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1
            }} 
            onClick={disabled ? undefined : action}
        >
            <CardContent>
                <Stack spacing={2}>
                    <Box
                        sx={{
                            backgroundColor: `${color}.light`,
                            borderRadius: "50%",
                            width: 48,
                            height: 48,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        {icon}
                    </Box>
                    <Typography variant="h6" component="div">
                        {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {description}
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );

    const AlertCard = ({ alert }) => {
        const getSeverityColor = (severity) => {
            switch (severity) {
            case "error": return "error";
            case "warning": return "warning";
            case "success": return "success";
            default: return "info";
            }
        };

        const getSeverityIcon = (severity) => {
            switch (severity) {
            case "error": return <ErrorIcon />;
            case "warning": return <WarningIcon />;
            case "success": return <CheckCircleIcon />;
            default: return <InfoIcon />;
            }
        };

        return (
            <Alert 
                severity={getSeverityColor(alert.severity)} 
                icon={getSeverityIcon(alert.severity)}
                sx={{ mb: 1 }}
            >
                <Typography variant="body2">
                    {alert.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {new Date(alert.timestamp).toLocaleString()}
                </Typography>
            </Alert>
        );
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress size={60} />
                </Box>
            </Container>
        );
    }

    const errorAlerts = getAlertsBySeverity("error");
    const warningAlerts = getAlertsBySeverity("warning");

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Stack spacing={4}>
                {/* Header */}
                <Box>
                    <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: "bold" }}>
                        Dashboard de Sistema
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        Bienvenido, {user?.displayName || user?.email}. 
                        Nivel de acceso: {permissions.getUserAccessLevel()}
                    </Typography>
                </Box>

                {/* Alertas críticas */}
                {errorAlerts.length > 0 && (
                    <Alert severity="error" icon={<ErrorIcon />}>
                        <Typography variant="h6" gutterBottom>
                            Errores del Sistema ({errorAlerts.length})
                        </Typography>
                        <Stack spacing={1}>
                            {errorAlerts.slice(0, 3).map((alert) => (
                                <AlertCard key={alert.id} alert={alert} />
                            ))}
                        </Stack>
                    </Alert>
                )}

                {warningAlerts.length > 0 && (
                    <Alert severity="warning" icon={<WarningIcon />}>
                        <Typography variant="h6" gutterBottom>
                            Advertencias ({warningAlerts.length})
                        </Typography>
                        <Stack spacing={1}>
                            {warningAlerts.slice(0, 3).map((alert) => (
                                <AlertCard key={alert.id} alert={alert} />
                            ))}
                        </Stack>
                    </Alert>
                )}

                {/* Estadísticas del sistema */}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Entidades Serializadas"
                            value={`${systemStats.serializedEntities.toLocaleString()}/${systemStats.totalEntities.toLocaleString()}`}
                            icon={<StorageIcon sx={{ color: "primary.main" }} />}
                            color="primary"
                            subtitle="Entidades con trazabilidad"
                            progress={(systemStats.serializedEntities / systemStats.totalEntities) * 100}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Migraciones Pendientes"
                            value={systemStats.pendingMigrations}
                            icon={<BugReportIcon sx={{ color: "warning.main" }} />}
                            color="warning"
                            subtitle="Requieren atención"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Errores del Sistema"
                            value={systemStats.systemErrors}
                            icon={<ErrorIcon sx={{ color: "error.main" }} />}
                            color="error"
                            subtitle="Errores activos"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Usuarios Activos"
                            value={systemStats.activeUsers}
                            icon={<CheckCircleIcon sx={{ color: "success.main" }} />}
                            color="success"
                            subtitle="Sesiones activas"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Uso de Almacenamiento"
                            value={`${systemStats.storageUsage}%`}
                            icon={<StorageIcon sx={{ color: "info.main" }} />}
                            color="info"
                            subtitle="Espacio utilizado"
                            progress={systemStats.storageUsage}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <StatCard
                            title="Uso de CPU"
                            value={`${systemStats.cpuUsage}%`}
                            icon={<SpeedIcon sx={{ color: "secondary.main" }} />}
                            color="secondary"
                            subtitle="Procesador"
                            progress={systemStats.cpuUsage}
                        />
                    </Grid>
                </Grid>

                {/* Acciones rápidas */}
                <Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", mb: 3 }}>
                        Acciones del Sistema
                    </Typography>
                    <Grid container spacing={3}>
                        {permissions.canManageSerialization() && (
                            <Grid item xs={12} sm={6} md={4}>
                                <QuickActionCard
                                    title="Dashboard de Serialización"
                                    description="Gestionar sistema de serialización"
                                    icon={<StorageIcon />}
                                    color="primary"
                                    action={() => window.location.href = "/admin/system/serialization-dashboard"}
                                />
                            </Grid>
                        )}
                        
                        {permissions.canViewTraceability() && (
                            <Grid item xs={12} sm={6} md={4}>
                                <QuickActionCard
                                    title="Trazabilidad de Entidades"
                                    description="Ver historial de cambios"
                                    icon={<SecurityIcon />}
                                    color="success"
                                    action={() => window.location.href = "/admin/system/entity-traceability"}
                                />
                            </Grid>
                        )}
                        
                        {permissions.canManageMigrations() && (
                            <Grid item xs={12} sm={6} md={4}>
                                <QuickActionCard
                                    title="Migración Masiva"
                                    description={`${systemStats.pendingMigrations} migraciones pendientes`}
                                    icon={<BugReportIcon />}
                                    color="warning"
                                    action={() => window.location.href = "/admin/system/bulk-migration"}
                                />
                            </Grid>
                        )}
                        
                        {permissions.hasPermission("system:view_logs") && (
                            <Grid item xs={12} sm={6} md={4}>
                                <QuickActionCard
                                    title="Logs del Sistema"
                                    description="Ver logs y auditoría"
                                    icon={<InfoIcon />}
                                    color="info"
                                    action={() => window.location.href = "/admin/system/system-logs"}
                                />
                            </Grid>
                        )}
                        
                        {permissions.hasPermission("system:view_performance") && (
                            <Grid item xs={12} sm={6} md={4}>
                                <QuickActionCard
                                    title="Monitoreo en Tiempo Real"
                                    description="Métricas de rendimiento"
                                    icon={<SpeedIcon />}
                                    color="secondary"
                                    action={() => window.location.href = "/admin/system/real-time-monitoring"}
                                />
                            </Grid>
                        )}
                        
                        {permissions.hasPermission("system:configure") && (
                            <Grid item xs={12} sm={6} md={4}>
                                <QuickActionCard
                                    title="Configuración"
                                    description="Configurar sistema"
                                    icon={<SecurityIcon />}
                                    color="primary"
                                    action={() => window.location.href = "/admin/system/general-config"}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Box>

                {/* Información de permisos */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Permisos de Sistema
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {permissions.getUserPermissions()
                                .filter(permission => permission.startsWith("system:"))
                                .map((permission) => (
                                    <Chip
                                        key={permission}
                                        label={permission}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>
        </Container>
    );
};

export default SystemDashboard; 